import Link from '@components/Link';
import SocialSubscribing, {
  SocialSubscribingProps,
} from '@components/SocialMediaLinks/SocialSubscribing';
import { getPhoneFormattedText } from '@util/phoneNumber';
import { FC } from 'react';
import { CardContentDetailList, CardHeader, CardImage } from '../BaseCard/BaseCard';
import { CardContentWrapper, CardWrapper, EmptyCardImage } from '../style';
import { LinksWrapper, WrapperSocials } from './style';
import { useTranslations } from 'next-intl';

export interface ShopCardProps {
  //image?: ResolvedImageData;
  image?: any;
  alt_text?: string;
  address?: string;
  title?: string;
  breNumber?: string;
  phone?: string;
  urlWebsite?: string;
  urlAdvisors?: string;
  urlContactUs?: string;
  urlFacebook?: string;
  urlTwitter?: string;
  urlLinkedin?: string;
  urlInstagram?: string;
  urlYoutube?: string;
  urlTiktok?: string;
  isShopGroupHomePage?: boolean;
  imageAspectRatio?: number;
  className?: string;
  dataTestId?: string;
}

//Social Media Icons and links
const MappedSocialSubscribing = ({ socialMedias }) => {
  const props: SocialSubscribingProps = {
    socialMedias: socialMedias,
  };
  return <SocialSubscribing {...props} />;
};

const ShopCard: FC<ShopCardProps> = ({
  image,
  alt_text = 'Shop Image',
  title,
  phone,
  address,
  breNumber,
  urlAdvisors,
  urlContactUs,
  urlWebsite,
  urlFacebook,
  urlTwitter,
  urlLinkedin,
  urlInstagram,
  urlTiktok,
  urlYoutube,
  isShopGroupHomePage,
  imageAspectRatio = 1, // 1:1 by default for shop cards
  className,
  dataTestId,
}) => {
  const t = useTranslations();
  //prepare social media links for mapping
  // if url is null or undefined, remove it from the array
  const socialMediaLinks = [
    { platform: 'facebook', url: urlFacebook },
    { platform: 'twitter', url: urlTwitter },
    { platform: 'linkedin', url: urlLinkedin },
    { platform: 'instagram', url: urlInstagram },
    { platform: 'tiktok', url: urlTiktok },
    { platform: 'youtube', url: urlYoutube },
  ].filter((socialMedia) => socialMedia.url);

  const formattedPhoneNumber = phone ? getPhoneFormattedText(phone) : undefined;

  return (
    <CardWrapper data-testid={dataTestId} className={className}>
      {image ? (
        <CardImage
          image={image}
          alt_text={alt_text}
          imageAspectRatio={imageAspectRatio}
          url={isShopGroupHomePage && urlContactUs ? urlContactUs : urlWebsite}
        />
      ) : (
        <EmptyCardImage />
      )}

      {title && (
        <CardHeader
          header={title}
          data-testid={`${dataTestId}-header`}
          url={isShopGroupHomePage && urlContactUs ? urlContactUs : urlWebsite}
        />
      )}

      <CardContentWrapper>
        <CardContentDetailList>
          {address && <li data-testid={`${dataTestId}-address`}>{address}</li>}

          {breNumber && <li data-testid={`${dataTestId}-bre`}>BRE: {breNumber}</li>}

          {formattedPhoneNumber && (
            <li>
              <a
                data-testid={`${dataTestId}-phone`}
                className="phone"
                href={`tel:${formattedPhoneNumber}`}
              >
                {formattedPhoneNumber}
              </a>
            </li>
          )}
        </CardContentDetailList>

        {socialMediaLinks && socialMediaLinks.length > 0 && (
          <WrapperSocials data-testid={`${dataTestId}-socials`}>
            <MappedSocialSubscribing socialMedias={socialMediaLinks} />
          </WrapperSocials>
        )}

        <LinksWrapper>
          {!isShopGroupHomePage && urlWebsite && (
            <Link href={urlWebsite} dataTestId="shop-locator-website-link">
              {t('Website')}
            </Link>
          )}
          {isShopGroupHomePage && urlContactUs && (
            <Link href={urlContactUs} dataTestId="shop-locator-contact-link">
              {t('Contact')}
            </Link>
          )}
          {urlAdvisors && (
            <Link href={urlAdvisors} dataTestId="shop-locator-advisors-link">
              {t('Advisors')}
            </Link>
          )}
        </LinksWrapper>
      </CardContentWrapper>
    </CardWrapper>
  );
};

export default ShopCard;
